import * as React from 'react';

import { ProductComparisonCommand } from '@app/components/comparisons/components/product-comparison-command';
import { SimpleDialog } from '@app/hooks/use-simple-dialog';
import { pcfRoutes } from '@app/pcf/pcf-routes';
import {
  useDeleteProduct,
  useDuplicateProduct,
} from '@shared/api/hooks/pcf/products';
import { ProductList, StatusEnum } from '@shared/api/types';
import Text from '@shared/components/content/text';
import { SimpleTooltip } from '@shared/components/simple-tooltip';
import { Button } from '@shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { useToast } from '@shared/components/ui/use-toast';
import sentry from '@shared/services/sentry';
import { Loader2, MoreHorizontal } from 'lucide-react';
import { generatePath, useNavigate } from 'react-router-dom';

interface ProductTableRowActionsProps {
  product: ProductList;
}

export function ProductTableRowActions({
  product,
}: ProductTableRowActionsProps): JSX.Element {
  const [open, setOpen] = React.useState(false);
  const { toast } = useToast();

  const navigate = useNavigate();

  const product_a_uuid: string = product.uuid;
  const product_a_status: string = product.status || '';
  const isRemote = !!product.remoteId;

  const { mutateAsync: deleteProduct, isLoading: isDeleting } =
    useDeleteProduct();

  const { mutateAsync: duplicateProduct, isLoading: isDuplicating } =
    useDuplicateProduct();

  const editPage = generatePath(pcfRoutes.PRODUCT_EDIT, {
    product_id: product_a_uuid,
  });

  const detailsPage = generatePath(pcfRoutes.PRODUCT_DETAIL, {
    product_id: product_a_uuid,
  });

  const onProductDelete = async () => {
    try {
      await deleteProduct({ productUuid: product_a_uuid });
      toast({
        description: 'Product deleted successfully.',
      });
    } catch (error) {
      sentry.log(error);
      toast({
        description: 'Failed to delete product.',
        variant: 'destructive',
      });
    }
  };

  const onProductDuplicate = async () => {
    try {
      const newProduct = await duplicateProduct({
        productUuid: product_a_uuid,
      });
      toast({
        description: 'Product duplicated successfully.',
        variant: 'success',
      });
      navigate(
        generatePath(pcfRoutes.PRODUCT_EDIT, {
          product_id: newProduct.uuid,
        })
      );
    } catch (error) {
      sentry.log(error);
      toast({
        description: 'Failed to duplicate product.',
        variant: 'destructive',
      });
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm">
          <span className="sr-only">Open menu</span>
          <MoreHorizontal className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="z-50 w-[200px]">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuGroup
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <DropdownMenuItem
            onClick={() => {
              (product_a_status === StatusEnum.in_progress &&
                product.assessmentDate === null) ||
              product_a_status === StatusEnum.draft
                ? navigate(editPage)
                : navigate(detailsPage);
            }}
          >
            Open
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              (product_a_status === StatusEnum.in_progress &&
                product.assessmentDate === null) ||
              product_a_status === StatusEnum.draft
                ? window.open(editPage, '_blank')
                : window.open(detailsPage, '_blank');
            }}
          >
            Open in new tab
          </DropdownMenuItem>
          {product_a_status !== StatusEnum.draft && (
            <DropdownMenuSub>
              <DropdownMenuSeparator />
              <DropdownMenuSubTrigger>Compare</DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="p-0">
                <ProductComparisonCommand
                  product_a={product_a_uuid}
                  setOpen={setOpen}
                />
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => onProductDuplicate()}>
            Duplicate
          </DropdownMenuItem>
          <SimpleTooltip
            disabled={!isRemote}
            trigger={
              <DropdownMenuItem
                disabled={isRemote}
                onClick={() => navigate(editPage)}
              >
                <Text
                  includeHover={isRemote}
                  hoverText="Product managed via integration"
                >
                  Edit
                </Text>
              </DropdownMenuItem>
            }
            content="Product managed via integration"
          />
          <SimpleDialog
            trigger={
              <DropdownMenuLabel
                className={`cursor-default rounded-sm font-normal hover:bg-accent ${isRemote ? 'pointer-events-none opacity-50' : 'text-destructive'}`}
              >
                <Text
                  includeHover={isRemote}
                  hoverText="Product managed via integration"
                >
                  Delete
                </Text>
              </DropdownMenuLabel>
            }
            header="Delete product"
            content={
              <p>
                Are you sure you want to delete this product?
                <p className="mt-2 font-bold">This action cannot be undone.</p>
              </p>
            }
            footer={
              <Button
                type="submit"
                variant="destructive"
                disabled={isDeleting}
                onClick={async () => await onProductDelete()}
              >
                Delete
                {isDeleting && (
                  <Loader2 size={14} className="ml-2 animate-spin" />
                )}
              </Button>
            }
          />
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
